import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  CardMedia,
  useTheme,
  alpha,
  ListItemIcon,
  Button,
} from '@mui/material';
import {
  Star as StarIcon,
  Visibility as VisionIcon,
  Lightbulb as ValueIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';

const About: React.FC = () => {
  const theme = useTheme();
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  // Team members data
  const teamMembers = [
    {
      name: 'Ashok Kumar',
      position: 'Founder & CEO',
      initials: 'AK',
      color: '#667eea',
      bio: 'Finance expert with 8+ years of experience in accounting and business management.',
    },
    {
      name: 'Ashwin Kumar',
      position: 'CTO',
      initials: 'AK',
      color: '#764ba2',
      bio: 'Young technologist at 26, specializing in fintech solutions and cloud infrastructure.',
    },
    {
      name: 'Monesh',
      position: 'CPO',
      initials: 'M',
      color: '#f093fb',
      bio: 'Product strategist passionate about creating intuitive financial tools for businesses.',
    },
    {
      name: 'Rahul',
      position: 'Head of Customer Success',
      initials: 'R',
      color: '#4facfe',
      bio: 'Dedicated to helping businesses optimize their accounting processes and growth.',
    },
  ];

  // Company values
  const companyValues = [
    {
      title: 'Transparency',
      description: 'We believe in complete transparency in pricing, features, and business operations.',
    },
    {
      title: 'Innovation',
      description: 'Constantly evolving our products to meet the changing needs of Indian businesses.',
    },
    {
      title: 'Customer First',
      description: 'Our customers are at the heart of everything we do. Their success is our success.',
    },
    {
      title: 'Simplicity',
      description: 'Making complex accounting processes simple and accessible for everyone.',
    },
  ];

  return (
    <>      <Box sx={{ overflow: 'hidden', pt: { xs: 10, md: 12 }, pb: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box textAlign="center" mb={8}>
              <Typography
                variant="h2"
                fontWeight="bold"
                gutterBottom
                sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}
              >
                About{' '}
                <Box
                  component="span"
                  sx={{
                    backgroundImage: 'var(--primary-gradient)',
                    backgroundClip: 'text',
                    color: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  AkMo Books
                </Box>
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}
              >
                Empowering Indian businesses with simple, intuitive accounting solutions
                to manage finances efficiently and drive growth.
              </Typography>
            </Box>
          </motion.div>

          {/* Our Story Section */}
          <Grid container spacing={6} mb={10} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Box>
                  <Typography variant="h4" fontWeight="bold" gutterBottom color="primary.main">
                    Our Story
                  </Typography>
                  <Typography variant="body1" paragraph>
                    AkMo Books was founded in 2025 by Ashok, Ashwin Kumar & Monesh with a simple mission: to simplify accounting
                    for Indian small businesses. Having experienced the challenges of financial management firsthand, they set out
                    to create a solution that combines power with simplicity.
                  </Typography>
                  <Typography variant="body1" paragraph>
                    What began as a simple bookkeeping tool has evolved into a comprehensive platform used by thousands of businesses
                    across India. Our growth has been driven by a deep understanding of the unique needs of Indian businesses and
                    a commitment to continuous innovation.
                  </Typography>
                  <Typography variant="body1">
                    Today, AkMo Books offers a complete suite of accounting tools designed specifically for the Indian market,
                    helping businesses of all sizes manage their finances, stay compliant with regulations, and make informed decisions.
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 4,
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                    height: { xs: 300, md: 400 },
                  }}
                >
                  <Box
                    component="img"
                    src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                    alt="Team collaborating"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Paper>
              </motion.div>
            </Grid>
          </Grid>

          {/* Mission, Vision, Values */}
          <Box mb={10}>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                  <motion.div variants={itemVariants}>
                    <Paper
                      elevation={3}
                      sx={{
                        p: 4,
                        height: '100%',
                        borderRadius: '16px',
                        background: `linear-gradient(135deg, ${alpha('#4776E6', 0.05)} 0%, ${alpha('#8E54E9', 0.1)} 100%)`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 12px 30px rgba(0, 0, 0, 0.1)',
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={3}>
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            mr: 2,
                          }}
                        >
                          <StarIcon fontSize="medium" />
                        </Box>
                        <Typography variant="h5" fontWeight="bold">
                          Our Mission
                        </Typography>
                      </Box>
                      <Typography variant="body1">
                        To empower Indian businesses with intuitive financial tools that simplify accounting,
                        ensure compliance, and drive growth, allowing entrepreneurs to focus on what they do best.
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <motion.div variants={itemVariants}>
                    <Paper
                      elevation={3}
                      sx={{
                        p: 4,
                        height: '100%',
                        borderRadius: '16px',
                        background: `linear-gradient(135deg, ${alpha('#4776E6', 0.05)} 0%, ${alpha('#8E54E9', 0.1)} 100%)`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 12px 30px rgba(0, 0, 0, 0.1)',
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={3}>
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            mr: 2,
                          }}
                        >
                          <VisionIcon fontSize="medium" />
                        </Box>
                        <Typography variant="h5" fontWeight="bold">
                          Our Vision
                        </Typography>
                      </Box>
                      <Typography variant="body1">
                        To be the leading accounting platform for Indian businesses, known for simplicity,
                        reliability, and innovation, helping create a financially empowered business ecosystem in India.
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <motion.div variants={itemVariants}>
                    <Paper
                      elevation={3}
                      sx={{
                        p: 4,
                        height: '100%',
                        borderRadius: '16px',
                        background: `linear-gradient(135deg, ${alpha('#4776E6', 0.05)} 0%, ${alpha('#8E54E9', 0.1)} 100%)`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 12px 30px rgba(0, 0, 0, 0.1)',
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={3}>
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            mr: 2,
                          }}
                        >
                          <ValueIcon fontSize="medium" />
                        </Box>
                        <Typography variant="h5" fontWeight="bold">
                          Our Approach
                        </Typography>
                      </Box>
                      <Typography variant="body1">
                        We believe in creating technology that adapts to how you work, not the other way around.
                        Our user-centric design approach ensures that our solutions are both powerful and
                        accessible to businesses at every stage of growth.
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              </Grid>
            </motion.div>
          </Box>

          {/* Core Values */}
          <Box mb={10}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Typography variant="h4" fontWeight="bold" textAlign="center" mb={6} color="primary.main">
                Our Core Values
              </Typography>
              
              <Grid container spacing={3}>
                {companyValues.map((value, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    >
                      <Paper
                        elevation={2}
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          height: '100%',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
                            transform: 'translateY(-5px)',
                          },
                        }}
                      >
                        <Box display="flex" alignItems="flex-start">
                          <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                            <CheckIcon color="primary" />
                          </ListItemIcon>
                          <Box>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                              {value.title}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                              {value.description}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Box>

          {/* Team Section */}
          <Box>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Typography variant="h4" fontWeight="bold" textAlign="center" mb={6} color="primary.main">
                Meet Our Leadership
              </Typography>
              
              <Grid container spacing={4}>
                {teamMembers.map((member, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                    >
                      <Card 
                        elevation={2} 
                        sx={{ 
                          borderRadius: 4,
                          overflow: 'hidden',
                          height: '100%',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 12px 30px rgba(0, 0, 0, 0.1)',
                          }
                        }}
                      >
                        <Box
                          sx={{
                            height: 200,
                            background: `linear-gradient(135deg, ${member.color} 0%, ${member.color}dd 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -50,
                              right: -50,
                              width: 100,
                              height: 100,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: -30,
                              left: -30,
                              width: 80,
                              height: 80,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            }}
                          />
                          <Typography
                            variant="h2"
                            fontWeight="bold"
                            color="white"
                            sx={{
                              fontSize: '3rem',
                              textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            }}
                          >
                            {member.initials}
                          </Typography>
                        </Box>
                        <CardContent>
                          <Typography variant="h6" fontWeight="bold">
                            {member.name}
                          </Typography>
                          <Typography variant="subtitle2" color="primary.main" gutterBottom>
                            {member.position}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {member.bio}
                          </Typography>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Box>
        </Container>
      </Box>

      {/* Footer */}    </>
  );
};

export default About; 